import { Injectable } from '@angular/core';
import {
  ApplicantAppEntityApplicantRead,
  ApplicantService,
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ApplicationAttachmentAppEntityApplicationRead,
  ApplicationAttachmentService,
  ApplicationService,
  AttachmentAttachmentRead,
  BusStopAppEntityBusStopRead,
  BusStopAppEntityBusStopWrite,
  BusStopService,
  ContractorAppEntityContractorRead,
  ContractorAppEntityContractorWrite,
  ContractorService,
  DrainageSurfaceCharacteristicAppEntityApplicationRead,
  DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite,
  DrainageSurfaceCharacteristicService,
  LocationDecisionAppEntityLocationDecisionWrite,
  LocationDecisionService,
  ParcelAppEntityParcelWrite,
  ParcelService,
  PatchApplicantApplicantItemRequestParams,
  RoadOccupationAppEntityRoadOccupationRead,
  RoadOccupationService,
  RoadOccupationStageService,
  ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead
} from '../openapi';
import { concatMap, forkJoin, map, Observable, of, switchMap, zip } from 'rxjs';
import { dateToPLString, toNumber } from './helpers';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from './application.common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  RoadOccupationFormValue
} from '../containers/applications/application/common/occupation-data-section/forms/road-occupation.form';
import {
  AdPlacementRoadOccupationFormValue
} from '../containers/applications/application/placement-advertising/sections/ad-placement-section/forms/ad-placement-road-occupation.form';
import {
  NotificationOfEmergencyOccupationFormValue
} from '../containers/applications/application/notification-of-emergency/sections/notification-of-emergency-occupation-data-section/forms/notification-of-emergency-occupation.form';
import {
  ContractorTag
} from '../containers/applications/application/common/contractor-data-section/contractor-data-section.component';
import {
  LocationOfDevicesUnrelatedToRoadOccupationFormValue
} from '../containers/applications/application/location-of-devices-unrelated-to-road/sections/location-of-devices-unrelated-to-road-occupation-section/forms/location-of-devices-unrelated-to-road-occupation.form';

export type ApplicationCode = 'roboczy' | 'zalatwiony' | 'wyslany' | 'zatwierdzony';

@Injectable({
  providedIn: 'root'
})
export class ApplicationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private applicantService: ApplicantService,
    private roadOccupationService: RoadOccupationService,
    private roadOccupationStageService: RoadOccupationStageService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private contractorService: ContractorService,
    private drainageSurfaceCharacteristicService: DrainageSurfaceCharacteristicService,
    private busStopService: BusStopService,
    private locationDecisionService: LocationDecisionService,
    private parcelService: ParcelService,
    private http: HttpClient
  ) {
  }

  public updateNotificationOfEmergencyOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: any): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));

    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });

    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(occupations.map((v: NotificationOfEmergencyOccupationFormValue) => {
          return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
            roadOccupation: {
              application: application?.uuid,
              typeOfFailure: v?.typeOfFailure
            }
          }).pipe(
            switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                v.stages.map(stage => {

                  const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));

                  return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                    switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                        roadOccupationStage: {
                          startDate: stage?.startDate || undefined,
                          endDate: stage?.endDate || undefined,
                          dayQuantity: stage?.dayQuantity || undefined,
                          geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                          provideRoadNumberAndName: stage.provideRoadNumberAndName,
                          city: stage.city,

                          roadName: stage?.roadName,
                          roadNumber: stage?.roadNumber,
                          street: stage?.street,

                          parcels: parcels.map(v => v?.uuid || '') || [],

                          roadArea: toNumber(stage?.roadArea) || undefined,
                          sidewalkArea: toNumber(stage?.sidewalkArea) || undefined,
                          bikePathArea: toNumber(stage?.bikePathArea) || undefined,
                          roadsideArea: toNumber(stage?.roadsideArea) || undefined,
                          greeneryArea: toNumber(stage?.greeneryArea) || undefined,
                          shoulderArea: toNumber(stage?.shoulderArea) || undefined,

                          roadOccupation: roadOccupation?.uuid
                        }
                      })
                    )
                  );
                })
              )
            )
          );
        }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateLocalisationData(
    application: ApplicationAppEntityApplicationRead,
    payload: ApplicationAppEntityApplicationWrite,
    parcels: ParcelAppEntityParcelWrite[],
    locationDecisions: LocationDecisionAppEntityLocationDecisionWrite[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = [
      ...(application.locationDecisions?.map(v => this.locationDecisionService.deleteLocationDecisionLocationDecisionItem({ id: v?.uuid || '' })) || []),
      ...(application.parcels?.map(v => this.parcelService.deleteParcelParcelItem({ id: v?.uuid || '' })) || [])
    ];

    const locationDecisionsPostRequests: Observable<any>[] = locationDecisions.map(locationDecision =>
      this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
        locationDecision
      }).pipe(map(v => v.uuid || ''))
    );
    const parcelsPostRequests: Observable<any>[] = parcels.map(parcel =>
      this.parcelService.postParcelParcelCollection({
        parcel
      }).pipe(map(v => v.uuid || ''))
    );
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() =>
        zip(
          (locationDecisionsPostRequests.length ? forkJoin(locationDecisionsPostRequests) : of([])),
          (parcelsPostRequests.length ? forkJoin(parcelsPostRequests) : of([]))
        ).pipe(
          switchMap(([locationIds, parcelIds]) =>
            this.applicationService.patchApplicationApplicationItem({
              id: application.uuid || '',
              application: {
                ...payload,
                parcels: parcelIds,
                locationDecisions: locationIds
              }
            })
          )
        )
      )
    );
  }

  public loadAttachmentPositionsByApplicationId(applicationId: string): Observable<AttachmentPositions> {
    return this.http.get<ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead[]>(`${environment.apiBasePath}/service-attachments-positions/by-application-id/${applicationId}?pagination=false`)
      .pipe(
        map(items => items.map(item => {
          return {
            tag: item?.tag || '',
            description: item?.name || '',
            required: !!item?.isRequired,
            multiple: !!item?.isMultiple,
            payment: item?.fee
          };
        }) || [])
      );
  }

  public updateBusOfStops(application: ApplicationAppEntityApplicationRead, listOfStops: BusStopAppEntityBusStopWrite[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.busStops || []).map(v => this.busStopService.deleteBusStopBusStopItem({
      id: v.uuid || ''
    }));
    const postRequests: Observable<BusStopAppEntityBusStopRead>[] = listOfStops.map(v => this.busStopService.postBusStopBusStopCollection({
      busStop: {
        ...v,
        application: application?.uuid || ''
      }
    }));
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(postRequests)),
      concatMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateAssureReceptionWater(application: ApplicationAppEntityApplicationRead | null, payload: ApplicationAppEntityApplicationWrite, drainageSurfaceCharacteristics: DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.drainageSurfaceCharacteristics || []).map((v: DrainageSurfaceCharacteristicAppEntityApplicationRead) => this.drainageSurfaceCharacteristicService.deleteDrainageSurfaceCharacteristicDrainageSurfaceCharacteristicItem({
        id: v.id || ''
      })
    );
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() =>
        forkJoin(
          drainageSurfaceCharacteristics.map(v => this.drainageSurfaceCharacteristicService.postDrainageSurfaceCharacteristicDrainageSurfaceCharacteristicCollection({
              drainageSurfaceCharacteristic: {
                ...v,
                application: application?.uuid
              }
            })
          )
        )
      ),
      concatMap(() => this.applicationService.patchApplicationApplicationItem({
        id: application?.uuid || '',
        application: {
          ...payload
        }
      }))
    );
  }

  public createApplication(serviceTypeId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    return forkJoin(applicants.map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({ applicant }))).pipe(
      switchMap(applicants =>
        this.applicationService.postApplicationApplicationCollection({
          application: {
            ...application,
            service: serviceTypeId,
            applicants: applicants.map(applicant => applicant?.uuid || '')
          }
        })
      )
    );
  }

  public updateApplicants(applicationId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    // for existing
    const applicantPatchRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !!v.id).map(item => this.applicantService.patchApplicantApplicantItem(item));

    // for new when id is empty string
    const applicantPostRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !v.id).map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({
      applicant
    }));
    return forkJoin([
      ...applicantPostRequests,
      ...applicantPatchRequests
    ]).pipe(
      switchMap((applicants) => this.applicationService.patchApplicationApplicationItem({
        id: applicationId,
        application: { ...application, applicants: applicants.map(applicant => applicant?.uuid || '') }
      }))
    );
  }

  public updateContractors(
    application: ApplicationAppEntityApplicationRead,
    payload: ApplicationAppEntityApplicationWrite,
    contractors: ContractorAppEntityContractorWrite[],
    tag: ContractorTag = 'investor'
  ): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests: Observable<any>[] = application?.contractors?.filter(v => v?.tag === tag)
      ?.map(v => this.contractorService.deleteContractorContractorItem({ id: v?.uuid || '' })) || [];
    if (contractors.length) {
      const postRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.map(contractor => this.contractorService.postContractorContractorCollection({
        contractor: {
          ...contractor,
          application: application?.uuid,
          tag
        }
      }));
      return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
        switchMap(() => forkJoin(postRequests)),
        concatMap(() => this.applicationService.patchApplicationApplicationItem({
          id: application?.uuid || '', application: { ...payload }
        }))
      );
    } else {
      return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
        switchMap(() => this.applicationService.patchApplicationApplicationItem({
          id: application?.uuid || '', application: payload
        }))
      );
    }
  }

  public updateOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: RoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));

    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });

    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(occupations.map(v => {
            return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
              roadOccupation: {
                application: application?.uuid,
                locationDecisionNumber: v?.locationDecisionNumber,
                locationDecisionDate: v?.locationDecisionDate || undefined,
                agreementNumber: v?.agreementNumber || undefined,
                agreementDate: v?.agreementDate || undefined,
                purpose: v?.purpose,
                occupationType: v?.occupationType,
                surfaceViolation: v?.surfaceViolation,
                workPurpose: v?.workPurpose,
                deviceType: v?.deviceType
              }
            }).pipe(
              switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                  v.stages.map(stage => {

                    const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));

                    return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                      switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                          roadOccupationStage: {
                            startDate: stage?.startDate || undefined,
                            endDate: stage?.endDate || undefined,
                            dayQuantity: stage?.dayQuantity || undefined,
                            geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                            provideRoadNumberAndName: stage.provideRoadNumberAndName,
                            city: stage.city,

                            roadName: stage?.roadName,
                            roadNumber: stage?.roadNumber,
                            street: stage?.street,

                            parcels: parcels.map(v => v?.uuid || '') || [],

                            roadWidth: toNumber(stage?.roadWidth) || undefined,
                            roadWidth2: toNumber(stage?.roadWidth2) || undefined,
                            roadWidth3: toNumber(stage?.roadWidth3) || undefined,

                            roadLength: toNumber(stage?.roadLength) || undefined,
                            roadLength2: toNumber(stage?.roadLength2) || undefined,
                            roadLength3: toNumber(stage?.roadLength3) || undefined,

                            roadArea: toNumber(stage?.roadArea) || undefined,
                            roadArea2: toNumber(stage?.roadArea2) || undefined,
                            roadArea3: toNumber(stage?.roadArea3) || undefined,
                            sidewalkArea: toNumber(stage?.sidewalkArea) || undefined,
                            sidewalkWidth: toNumber(stage?.sidewalkWidth) || undefined,
                            sidewalkLength: toNumber(stage?.sidewalkLength) || undefined,
                            otherElementsArea: toNumber(stage?.otherElementsArea) || undefined,
                            otherElementsWidth: toNumber(stage?.otherElementsWidth) || undefined,
                            otherElementsLength: toNumber(stage?.otherElementsLength) || undefined,
                            roadOccupation: roadOccupation?.uuid
                          }
                        })
                      )
                    );
                  })
                )
              )
            );
          })
        );
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateLocationOfDevicesUnrelatedToRoadOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: LocationOfDevicesUnrelatedToRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(occupations.map(v => {
            return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
              roadOccupation: {
                application: application?.uuid,
                locationDecisionNumber: v?.locationDecisionNumber,
                locationDecisionDate: v?.locationDecisionDate || undefined,
                deviceType: v?.deviceType
              }
            }).pipe(
              switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                  v.stages.map(stage => {

                    const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));

                    return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                      switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                          roadOccupationStage: {
                            startDate: stage?.startDate || undefined,
                            endDate: stage?.endDate || undefined,
                            dayQuantity: stage?.dayQuantity || undefined,

                            devicesProvidePlacementDeadline: stage.devicesProvidePlacementDeadline,

                            nextYearsDateFrom: stage?.nextYearsDateFrom || undefined,
                            nextYearsDateTo: stage?.nextYearsDateTo || undefined,
                            yearsCount: stage?.yearsCount || undefined,

                            provideRoadNumberAndName: stage.provideRoadNumberAndName,
                            city: stage.city,

                            roadName: stage?.roadName,
                            roadNumber: stage?.roadNumber,
                            street: stage?.street,

                            parcels: parcels.map(v => v?.uuid || '') || [],

                            devicesCountyRoadsArea: toNumber(stage?.devicesCountyRoadsArea) || undefined,
                            devicesOnBridgeArea: toNumber(stage?.devicesOnBridgeArea) || undefined,
                            infrastructureEquipmentArea: toNumber(stage?.infrastructureEquipmentArea) || undefined,
                            roadOccupation: roadOccupation?.uuid
                          }
                        })
                      )
                    );
                  })
                )
              )
            );
          })
        );
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateAdPlacementOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: AdPlacementRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    application?.roadOccupations?.forEach(occupation => {

      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({ id: parcel?.uuid || '' }));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(
          occupations.reverse().map((occupationFormValue: AdPlacementRoadOccupationFormValue) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
              roadOccupation: {
                application: application?.uuid
              }
            }).pipe(
              switchMap((occupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                occupationFormValue.stages.map(stage => {
                  const createParcelsRequests = stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({ parcel }));
                  return (createParcelsRequests.length ? forkJoin(createParcelsRequests) : of([])).pipe(
                    switchMap((parcels) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                        roadOccupationStage: {
                          startDate: stage?.startDate || undefined,
                          endDate: stage?.endDate || undefined,
                          dayQuantity: stage.dayQuantity || 0,

                          adType: stage.adType || undefined,
                          adWidth: toNumber(stage.adWidth),
                          adLength: toNumber(stage.adLength),
                          adQuantity: toNumber(stage.adQuantity || undefined),
                          adArea: toNumber(stage.adArea),

                          geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                          provideRoadNumberAndName: stage.provideRoadNumberAndName,
                          city: stage.city,
                          roadName: stage?.roadName || '',
                          roadNumber: stage?.roadNumber || '',
                          street: stage?.street || '',
                          adContent: stage.adContent,

                          parcels: parcels.map(v => v?.uuid || '') || [],
                          roadOccupation: occupation?.uuid
                        }
                      })
                    )
                  );
                })
              ))
            )
          )
        )
      ),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateApplicationAttachments(
    application: ApplicationAppEntityApplicationRead, attachments: AttachmentAttachmentRead[]
  ): Observable<any> {
    if (!attachments.length) {
      return this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' });
    }
    const oldIds: string[] = application?.attachments?.map((v: ApplicationAttachmentAppEntityApplicationRead) => v.id || '') || [];
    if (oldIds?.length) {
      return forkJoin(oldIds.map(v => this.applicationAttachmentService.deleteApplicationAttachmentItemApplicationAttachmentItem({
        id: v
      }))).pipe(
        switchMap(() => forkJoin(attachments?.map(attachment => this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
          applicationAttachment: {
            application: application?.uuid,
            attachment: attachment?.id
          }
        }))).pipe(
          switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
        ))
      );
    } else {
      return forkJoin(
        attachments?.map(attachment =>
          this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
            applicationAttachment: {
              application: application?.uuid,
              attachment: attachment?.id
            }
          })
        )
      ).pipe(
        switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
      );
    }
  }

  public loadApplicationList(code: ApplicationCode): Observable<ApplicationListItem[]> {
    return this.applicationService.getApplicationCollectionApplicationCollection({
      pagination: false,
      statusCode: code,
      orderUpdatedAt: code === 'roboczy' ? 'desc' : undefined,
      orderApprovedAt: code === 'zatwierdzony' ? 'desc' : undefined,
      orderSentAt: code === 'wyslany' || code === 'zalatwiony' ? 'desc' : undefined
    }).pipe(
      map((applications: Array<ApplicationAppEntityApplicationRead>) => applications.filter(v => !!v?.service).map(v => ({
          tag: `${v?.service?.name || ''} ${dateToPLString(v?.createdAt) || ''} ${dateToPLString(v?.updatedAt) || ''} ${dateToPLString(v?.sentAt) || ''} ${dateToPLString(v?.approvedAt) || ''}`,
          application: v
        }))
      )
    );
  }

  public loadApplicationPayments(applicationId: string): Observable<ApplicationPayment> {
    return this.http.post<ApplicationPayment>(`${environment.apiBasePath}/application/${applicationId}/cost-forecast`, {});
  }

  public signApplication(applicationId: string): Observable<{ result: string; redirect_url: string }> {
    return this.http.get<{
      result: string;
      redirect_url: string
    }>(`${environment.apiBasePath}/application/${applicationId}/sign-attachments`);
  }

}
